import type { TFunction } from 'i18next';
import { useTranslation } from 'react-i18next';
import type { ITaskStockClaim } from '@interfaces/IScheduler';
import type { InventoryLevel, UntrackedMaterial } from '@interfaces/ILogistics';
import { estimatedOnHand } from '@d-orca/shared/client/logistics';
import { needOf, type ClaimOutcome, type IClaimEvaluation } from '../../stock/claimEvaluation';
import { InventoryRowMain, InventoryRowGauge } from '../../../LogisticsPage/panels/InventoryRowCore';
import { rowActivationProps } from '../../../LogisticsPage/panels/rowActivation';
import { InventoryUntrackedRow } from '../../../LogisticsPage/panels/InventoryUntrackedRow';
import { bookingCovers, type IOwnBooking } from '../../stock/joinOrder';
import styles from './TaskPanelStockTab.module.css';

interface IStockClaimOutcomeProps {
  claim: ITaskStockClaim;
  evaluation: IClaimEvaluation;
  /** This task's booking on the open delivery for the material — the order
   *  and the task's share of its line (`useTaskStock.bookingFor`). With one
   *  covering the need, the gap has been acted on: the box turns from a
   *  shortfall into the order's state. */
  booking: IOwnBooking | null;
  /** Inventory Levels' own "not tracked" row for the material, when an order
   *  exists but no stock has been recorded (`useTaskStock.untrackedFor`). */
  untracked: UntrackedMaterial | null;
  /** The LIST's own status line for this material, when a gap has an answer
   *  on its way ("No stock on site · #DEL-902 is on its way"). It REPLACES
   *  the stock sentence: one line, in words the tab already uses, instead of
   *  two paragraphs saying the same thing (Niv, 2026-09-16). */
  onItsWay: string | null;
  tz: string;
  /** Go to the delivery on the Logistics page (the untracked row's jump and
   *  its "Add stock", which lives there). Absent in hosts without a router. */
  onOpenDelivery?: (deliveryRef: string) => void;
  /** Open the level row's record beside the drawer (the site map's own
   *  row-tap → stock companion). */
  onOpenStock: (level: InventoryLevel) => void;
}

/** The tone each of the six `ClaimOutcome` kinds reads in when the box is
 *  showing a stock read (the CAD ceiling, §5.2, is a hard limit that wins over
 *  any stock read, so 'over' reads as an error exactly like 'none'). */
const TONE_BY_KIND: Record<ClaimOutcome['kind'], 'idle' | 'ok' | 'warn' | 'error'> = {
  idle: 'idle',
  over: 'error',
  none: 'error',
  'unit-mismatch': 'warn',
  ok: 'ok',
  partial: 'warn',
};

function outcomeMessage(t: TFunction, claim: ITaskStockClaim, outcome: ClaimOutcome): string {
  switch (outcome.kind) {
    case 'idle':
      return claim.unit === ''
        ? t('scheduler.panel.stock.idleUnit')
        : t('scheduler.panel.stock.idle');
    case 'over':
      // The ceiling is per element, so the message names the first type the
      // claim can no longer fully have — and who took it, when someone did.
      return outcome.takenBy === null
        ? t('scheduler.panel.stock.goneElements', { count: outcome.conflicting, kind: outcome.elementKind })
        : t('scheduler.panel.stock.overElements', { count: outcome.conflicting, kind: outcome.elementKind, task: outcome.takenBy });
    case 'none':
      return t('scheduler.panel.stock.none', {
        material: claim.materialName,
        qty: claim.quantity,
        unit: claim.unit,
      });
    case 'unit-mismatch':
      return t('scheduler.panel.stock.unitMismatch', {
        invUnit: outcome.level.unit,
        amount: estimatedOnHand(outcome.level),
        qty: claim.quantity,
        unit: claim.unit,
      });
    case 'ok':
      return t('scheduler.panel.stock.ok', {
        amount: estimatedOnHand(outcome.level),
        unit: claim.unit,
        qty: claim.quantity,
      });
    case 'partial':
      return t('scheduler.panel.stock.partial', {
        amount: estimatedOnHand(outcome.level),
        unit: claim.unit,
        qty: claim.quantity,
        short: outcome.short,
      });
  }
}

/** The stock-check outcome for the claim being edited: the tone box and the
 *  reused Logistics inventory row (ok / partial / unit-mismatch only — 'none'
 *  has no level to show, and 'over' is a ceiling failure the stock read never
 *  runs for). Read only: ordering happens once, at the tab's checkout
 *  (contract v14), never from a row. */
export function StockClaimOutcome({ claim, evaluation, booking, untracked, onItsWay, tz, onOpenDelivery, onOpenStock }: IStockClaimOutcomeProps) {
  const { t } = useTranslation();
  const { outcome } = evaluation;
  const showsInventory = outcome.kind === 'ok' || outcome.kind === 'partial' || outcome.kind === 'unit-mismatch';
  // A gap with an order already raised for it is no longer red: the order is
  // the answer, and it is shown instead of the shortfall. The ceiling failure
  // and the blank row are not gaps and stay as they are.
  const gap = outcome.kind === 'none' || outcome.kind === 'partial';
  // "Ordered" is a matter of amount, not of existence (contract v13): the
  // task's share of the line has to cover what the claim still needs.
  const ordered = bookingCovers(booking, needOf(outcome));
  // No stock yet but an order raised: the box shows Inventory Levels' own
  // "not tracked" row for the material — the same row, the same words — in a
  // plain box, not a red one. A partial read keeps its amber and its level row.
  const showsUntracked = ordered && outcome.kind === 'none';
  // A gap the checkout can answer is not an error: the order exists and the
  // add is one step away, so the box reads plain and says so below.
  const tone = showsUntracked ? 'idle' : ordered && gap ? 'ok' : onItsWay !== null ? 'idle' : TONE_BY_KIND[outcome.kind];

  return (
    <div className={styles.outcomeBox} data-tone={tone}>
      {/* "No stock" is not news once the order exists — the material's
          Inventory Levels row replaces it; any other read stays. */}
      {!showsUntracked && <p>{onItsWay ?? outcomeMessage(t, claim, outcome)}</p>}

      {showsUntracked && untracked !== null && (
        <ul className={styles.untrackedList}>
          <InventoryUntrackedRow
            material={untracked}
            tz={tz}
            onJumpToDelivery={(ref) => onOpenDelivery?.(ref)}
            // Recording stock is the Logistics page's job; go there.
            onAddStock={(m) => onOpenDelivery?.(m.deliveryRef)}
            // Add stock here only navigates; the Logistics page gates the write.
            canEdit
          />
        </ul>
      )}

      {showsInventory && (
        /* The level row is the tab's one way into the record: a tap (or
           Enter/Space) opens it in the companion docked beside the drawer,
           exactly as the site map's area drawer does — the same shared
           `rowActivationProps` idiom, named by the material like its rows. */
        <div
          className={styles.inventoryBox}
          {...rowActivationProps(() => onOpenStock(outcome.level), outcome.level.materialName)}
        >
          <div className={styles.inventoryMain}>
            <InventoryRowMain level={outcome.level} tz={tz} />
          </div>
          <InventoryRowGauge level={outcome.level} tz={tz} />
        </div>
      )}

      {outcome.kind === 'ok' && (
        <p className={styles.claimLine}>
          {t('scheduler.panel.stock.claimLine', { qty: claim.quantity, unit: claim.unit, remains: outcome.remains })}
        </p>
      )}

      {/* What happens next, for a gap an order on its way can answer: how much
          of this booking that order already carries, and what the checkout
          adds to it. */}
      {/* "Order more" only while nothing is on order for it at all — with a
          booking the checkout names the order and what it adds to it. */}
      {outcome.kind === 'partial' && booking === null && (
        <p className={styles.gapLine}>
          {t('scheduler.panel.stock.gapLine', { short: outcome.short, unit: claim.unit })}
        </p>
      )}
    </div>
  );
}
